import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { useEffect, useRef, useState } from 'react';
import {
    Cluster,
    GridAlgorithm,
    MarkerClusterer,
    Renderer,
} from '@googlemaps/markerclusterer';
import { Snackbar } from '../Snackbar';
import { MapWrapper } from '../MapWrapper';
import { MapElement } from '../MapElement';
import { InfoWindow } from '../InfoWindow';
import { useStateContext, useGoogleMapsContext } from '../../context';
import { ILocation, IMarker, IAvailableStore } from '../../types';
import { icons, MAP_STYLES } from '../../constants';
import { dataLayerStoreEvent } from '../../utils';
import { useSnackbar } from '../../hooks';

interface IProps {
    className?: string;
}

const BaseGoogleMaps = ({ className }: IProps) => {
    const [storeInArea, setStoreInArea] = useState(false);
    const mapRef = useRef<HTMLDivElement | null>(null);
    const markerClickStore = useRef<IAvailableStore | undefined>(undefined);
    const { snackbarState, setSnackbarState, resetSnackbarState } =
        useSnackbar();
    const { googleMapsRef, setCurrentLocationMarker, setMapObject } =
        useGoogleMapsContext();
    const {
        availableStores,
        centerLocation,
        mobileView,
        searchLocation,
        placeId,
        selectedStore,
        useCurrentLocation,
        showMobileMap,
        setFindingCurrentLocation,
        setSelectedStore,
        setSearchLocation,
        setSearchInputValue,
        setUseCurrentLocation,
    } = useStateContext();

    const { maps } = google;

    const setPlaceMapBounds = async () => {
        if (!googleMapsRef.current.mapObject) return;

        try {
            const { results } = await googleMapsRef.current.geocoder.geocode({
                placeId: placeId,
            });

            const result = results[0];
            const {
                location: { lat, lng },
                viewport,
            } = result.geometry;

            const searchLocation = {
                lat: lat(),
                lng: lng(),
            };

            setSearchLocation(searchLocation);
            setSearchInputValue(result.formatted_address);

            const cityLatLng = new google.maps.LatLng(
                searchLocation.lat,
                searchLocation.lng
            );

            let bounds: google.maps.LatLngBounds;
            if (viewport) {
                bounds = viewport;
            } else {
                bounds = new google.maps.LatLngBounds();
                bounds.extend(cityLatLng);
            }

            const storeInBounds = availableStores?.some((store) =>
                bounds.contains(new google.maps.LatLng(store.lat, store.lng))
            );

            setStoreInArea(!!storeInBounds);
        } catch {
            setSnackbarState({
                open: true,
                message: 'Deze locatie kunnen we niet vinden',
                variant: 'error',
            });

            setDefaultMapBounds();
        }
    };

    const setDefaultMapBounds = () => {
        if (!googleMapsRef.current.mapObject) return;

        if (mobileView) {
            googleMapsRef.current.mapObject.setCenter({
                lat: 52.1326,
                lng: 5.2913,
            });
            googleMapsRef.current.mapObject.setZoom(8);
        } else {
            googleMapsRef.current.mapObject.fitBounds(
                googleMapsRef.current.defaultBounds,
                0
            );
        }
    };

    const addMarkers = () => {
        if (!googleMapsRef.current.mapObject || !availableStores) return;

        const map = googleMapsRef.current.mapObject;

        // Add new markers
        googleMapsRef.current.markers = availableStores.map((store) => {
            const { lat, lng } = store;
            const storePosition = {
                lat,
                lng,
            };

            const marker = new maps.Marker({
                position: storePosition,
                icon: {
                    url: icons.marker,
                },
                map,
            });

            marker.addListener('mouseover', () => {
                marker.setIcon({
                    url: icons.markerHover,
                });
            });

            marker.addListener('mouseout', () => {
                marker.setIcon({
                    url: icons.marker,
                });
            });

            marker.addListener('click', () => {
                markerClickStore.current = store;

                setSelectedStore(store);
                panToPosition(storePosition);

                dataLayerStoreEvent(store, 0, 'map click - marker');

                if (googleMapsRef.current.infoWindow !== undefined) {
                    googleMapsRef.current.infoWindow.open(map, marker);
                }
            });

            return {
                store,
                marker,
            };
        });

        const markers = googleMapsRef.current.markers.map(
            (marker: IMarker) => marker.marker
        );

        const customRenderer: Renderer = {
            render: ({ count, position }) => {
                return new google.maps.Marker({
                    position,
                    icon: {
                        url: 'https://grandvision.s3.amazonaws.com/pblx/images/gnl-cluster.svg',
                        scaledSize: new google.maps.Size(24, 24),
                        labelOrigin: new google.maps.Point(12, 12),
                    },
                    label: {
                        text: String(count),
                        color: 'white',
                        fontWeight: '700',
                        fontSize: '12px',
                    },
                    zIndex: google.maps.Marker.MAX_ZINDEX! + count,
                });
            },
        };

        const customAlgorithm = new GridAlgorithm({
            maxZoom: 12,
            gridSize: 40,
        });

        googleMapsRef.current.markerClusterer = new MarkerClusterer({
            map,
            markers,
            renderer: customRenderer,
            algorithm: customAlgorithm,
        });

        if (googleMapsRef.current.markerClusterer) {
            maps.event.addListener(
                googleMapsRef.current.markerClusterer,
                'clusterclick',
                handleMarkerClusterClick
            );
        }
    };

    const handleMarkerClusterClick = (cluster: Cluster) => {
        googleMapsRef.current.geocoder.geocode(
            { location: cluster.position },
            () => {}
        );
    };

    const handleUseCurrentLocation = () => {
        setFindingCurrentLocation(true);

        const geoLocationOptions = {
            timeout: 5000,
        };

        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const location = {
                    lat: pos.coords.latitude,
                    lng: pos.coords.longitude,
                };

                setSearchLocation(location);
                setFindingCurrentLocation(false);
                setUseCurrentLocation(true);
                setCurrentLocationMarker(location);
            },
            () => {
                setFindingCurrentLocation(false);
                setUseCurrentLocation(false);

                setSnackbarState({
                    open: true,
                    message: 'We kunnen je huidige locatie niet vinden',
                    variant: 'error',
                });
            },
            geoLocationOptions
        );
    };

    const panToPosition = (location: ILocation) => {
        if (!googleMapsRef.current.mapObject) return;

        googleMapsRef.current.mapObject.panTo(location);
        const zoomLevel = storeInArea ? 13 : 10;
        googleMapsRef.current.mapObject.setZoom(zoomLevel);
    };

    // Init map based on available stores
    useEffect(() => {
        if (
            !mapRef.current ||
            googleMapsRef.current.mapObject ||
            !availableStores
        )
            return;

        const map = new maps.Map(mapRef.current, {
            disableDefaultUI: true,
            styles: MAP_STYLES,
            cameraControl: true,
            zoomControl: mobileView ? false : true,
        });

        setMapObject(map);

        maps.event.addListenerOnce(map, 'idle', () => {
            placeId ? setPlaceMapBounds() : setDefaultMapBounds();
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [availableStores]);

    useEffect(() => {
        if (
            !searchLocation ||
            !googleMapsRef.current.mapObject ||
            !availableStores?.length
        )
            return;

        addMarkers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchLocation, availableStores]);

    useEffect(() => {
        if (useCurrentLocation) {
            handleUseCurrentLocation();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [useCurrentLocation]);

    // Reset bounds if placeId changes
    useEffect(() => {
        if (!placeId) {
            setDefaultMapBounds();
            setSearchLocation(undefined);

            return;
        }

        setUseCurrentLocation(false);
        setSelectedStore(undefined);
        setPlaceMapBounds();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [placeId]);

    // Pan to search or center location
    useEffect(() => {
        if (searchLocation !== undefined) {
            panToPosition(searchLocation);
        } else if (centerLocation !== undefined) {
            panToPosition(centerLocation);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchLocation, centerLocation]);

    // Trigger resize for mobile
    useEffect(() => {
        if (!mapRef.current) return;

        const map = googleMapsRef.current.mapObject;

        if (!map) return;

        const shouldResize =
            (!mobileView && mapRef.current.offsetParent !== null) ||
            (mobileView && showMobileMap);

        if (shouldResize) {
            setTimeout(() => {
                google.maps.event.trigger(map, 'resize');
                setDefaultMapBounds();
            }, 100);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mobileView, showMobileMap]);

    useEffect(() => {
        const interval = setInterval(() => {
            const map = googleMapsRef.current.mapObject;
            const infoWindow = googleMapsRef.current.infoWindow;

            // Prod fix: Wait until both exist before attaching listener
            // otherwise infowindow will not close
            if (map && infoWindow) {
                clearInterval(interval);

                const handleMapClick = () => infoWindow.close();

                const listener = map.addListener('click', handleMapClick);

                return () => listener.remove();
            }
        }, 100);

        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            <Snackbar {...snackbarState} onClose={resetSnackbarState} />
            <MapWrapper className={className}>
                <MapElement ref={mapRef} />
                {selectedStore &&
                    ReactDOM.createPortal(
                        <InfoWindow />,
                        googleMapsRef.current.infoWindowNode
                    )}
            </MapWrapper>
        </>
    );
};

export const GoogleMaps = styled(BaseGoogleMaps)``;
